import { useAction, useAtom } from '@reatom/npm-react'
import { lazy, memo, Suspense, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router'
import styled from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { Network } from '@root/entities/network'
import { reatomPairResource } from '@root/entities/pair/atoms/reatom-pair-resource'
import { PairAddress } from '@root/entities/pair/model'
import { changeTradingPairAction } from '@root/features/trade/trading'
import {
  reatomBuyControls,
  reatomSellControls,
} from '@root/features/trade/trading-controls/atoms/common'

import { BoardNav } from '../pulse-screen/board-nav'
import { PulseFooter } from '../pulse-screen/pulse-footer'
import { pulseV2Styles } from '../pulse-screen/pulse-v2.styles'

import { reatomPairHolders } from './pair-makers'
import { reatomPairTrades } from './pair-trades'
import { TradeBar } from './trade-bar'
import { TradeHeader } from './trade-header'
import { FlowPanel, TradePanel } from './trade-panel'
import { ClonePanel, DevPanel, SecurityPanel, TrackedPanel } from './trade-rail'
import { RiskPanel } from './trade-risk'
import { SideTape } from './trade-side-tape'
import { PairTables } from './trade-tape'
import { tradeStyles } from './trade.styles'

/*
 * The same chart the old page draws, loaded the same way it loads it: it is a
 * megabyte of charting and nothing else on this screen needs it.
 */
const TradeChart = lazy(async () => ({
  default: (
    await import('@root/widgets/trade/trade-chart/ui/desktop/trade-chart')
  ).TradeChart,
}))

/**
 * One token, in the board's language.
 *
 * The page it replaces is the terminal's oldest screen and reads like it: a
 * strip of a dozen grey figures, all the same size, above a chart. Both of the
 * terminals we are measured against do the same thing. What is different here
 * is what leads — one figure at the size it deserves, two sentences where
 * there were two fractions, and colour only on the readings that carry a
 * judgement.
 *
 * Built in pieces, like the board was. The header is the piece that decides
 * whether the rest is worth putting beside it.
 */
export const TradeScreen: React.FC = memo(() => {
  const { pairAddress, network: networkParam } = useParams()

  const network = useMemo(
    () => Network.fromPath(networkParam ?? '') ?? Network.Network.Solana,
    [networkParam],
  )

  const resource = useMemo(
    () =>
      pairAddress === undefined
        ? undefined
        : // eslint-disable-next-line @reatom/unit-naming-rule
          reatomPairResource({
            pairAddress: PairAddress(pairAddress),
            network,
            loadOnConnect: true,
          }),
    [pairAddress, network],
  )

  const [state] = useAtom(
    (ctx) => (resource ? ctx.spy(resource) : undefined),
    [resource],
  )

  const pair =
    state === undefined
      ? undefined
      : AsyncStateN.matchWaiting(state, {
          Waiting: () => undefined,
          Error: () => undefined,
          Success: (value) => value,
        })

  /*
   * The trading atoms are global — which pair is being traded is app state,
   * not a prop. The old page keeps them in step through a router sync bound to
   * its own route, so this one says it plainly: the pair in this url is the
   * pair being traded. Without it the chart's overlays would be drawn from
   * whichever token was open last.
   */
  const changeTradingPair = useAction(changeTradingPairAction)
  useEffect(() => {
    if (pairAddress !== undefined) {
      changeTradingPair(PairAddress(pairAddress))
    }
  }, [pairAddress, changeTradingPair])

  const tradesAtom = useMemo(
    () =>
      pairAddress === undefined
        ? undefined
        : // eslint-disable-next-line @reatom/unit-naming-rule
          reatomPairTrades({
            pairAddress: PairAddress(pairAddress),
            network,
          }),
    [pairAddress, network],
  )
  const [trades] = useAtom(
    (ctx) => (tradesAtom ? ctx.spy(tradesAtom) : []),
    [tradesAtom],
  )

  /*
   * Where the tape sits, which is a preference and not state the server owns.
   * Beside the chart by default because that is where it is read from; folded
   * when somebody wants the chart's width back; below when they would rather
   * have the wide table and nothing in the way.
   */
  const [tapeMode, setTapeMode] = useState<'beside' | 'folded' | 'below'>(
    'beside',
  )

  /*
   * One holders read for the page. The rail's tracked panel needs it to say
   * what each followed wallet is holding, and the Holders tab needs the same
   * rows — asking twice would be two requests for one answer.
   */
  const holdersResource = useMemo(
    () =>
      pairAddress === undefined
        ? undefined
        : // eslint-disable-next-line @reatom/unit-naming-rule
          reatomPairHolders({
            pairAddress: PairAddress(pairAddress),
            network,
          }),
    [pairAddress, network],
  )

  // eslint-disable-next-line @reatom/unit-naming-rule
  const buyControlsModel = useMemo(() => reatomBuyControls(), [])
  // eslint-disable-next-line @reatom/unit-naming-rule
  const sellControlsModel = useMemo(() => reatomSellControls(), [])

  return (
    <Screen>
      <div className="app">
        <div className="glow" />

        <BoardNav active="discover" />

        {/* The bar Pulse and Portfolio already have, carrying the strip of
            other tokens — the only thing on this screen that says the rest of
            the market is still moving. */}
        <TradeBar network={network} pairAddress={pairAddress} />

        <div className="page">
          {pair ? (
            <TradeHeader pair={pair} />
          ) : (
            <header className="tokenhead">
              <span className="full">Reading this token…</span>
            </header>
          )}

          <div className="main">
            <div className="chartrow">
              <div className="chartbox">
                {/*
                  The artboard puts this at the right end of the chart's
                  toolbar. That toolbar belongs to the chart package, so the
                  control sits in the free span of the same row instead —
                  the placement the design asks for, without reaching into a
                  shared widget to get it.
                */}
                <button
                  className="charttool"
                  type="button"
                  title={
                    tapeMode === 'beside'
                      ? 'Put the tape under the chart instead'
                      : 'Put the tape back beside the chart'
                  }
                  onClick={() =>
                    setTapeMode(tapeMode === 'beside' ? 'below' : 'beside')
                  }
                >
                  <svg
                    width="11"
                    height="11"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinejoin="round"
                  >
                    <rect x="3" y="3" width="18" height="18" rx="2" />
                    <path d="M15 3 L15 21" />
                  </svg>
                  {tapeMode === 'beside' ? 'Tape beside' : 'Tape below'}
                </button>

                {pair ? (
                  <Suspense
                    fallback={<p className="soon">Drawing the chart…</p>}
                  >
                    <TradeChart
                      pair={pair}
                      buyControlsModel={buyControlsModel}
                      sellControlsModel={sellControlsModel}
                    />
                  </Suspense>
                ) : (
                  <p className="soon">Reading this token…</p>
                )}
              </div>

              {pair && tapeMode === 'beside' ? (
                <SideTape
                  pair={pair}
                  trades={trades}
                  onFold={() => setTapeMode('folded')}
                  onMoveBelow={() => setTapeMode('below')}
                />
              ) : undefined}

              {/*
                Whenever the tape is not beside the chart, the seam keeps a
                hand's width of it so the way back is where it went. This was
                a one-way door until it was clicked: moving the tape below hid
                the tape and the control that moved it, together.
              */}
              {tapeMode === 'beside' ? undefined : (
                <button
                  className="tapestub"
                  type="button"
                  title="Put the tape back beside the chart"
                  onClick={() => setTapeMode('beside')}
                >
                  <svg
                    width="9"
                    height="9"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.6"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M15 6 L9 12 L15 18" />
                  </svg>
                  <span className="vert">TAPE</span>
                  <span className="live" />
                </button>
              )}
            </div>

            <PairTables
              pair={pair}
              pairAddress={
                pairAddress === undefined ? undefined : PairAddress(pairAddress)
              }
              network={network}
              trades={trades}
              holders={holdersResource}
            />
          </div>

          <div className="side">
            {pair ? (
              <>
                <FlowPanel pair={pair} />
                {holdersResource ? (
                  <TrackedPanel
                    pair={pair}
                    trades={trades}
                    holders={holdersResource}
                  />
                ) : undefined}
                <TradePanel
                  pair={pair}
                  buyModel={buyControlsModel}
                  sellModel={sellControlsModel}
                />
                <RiskPanel pair={pair} />
                <SecurityPanel pair={pair} />
                <ClonePanel pair={pair} />
                <DevPanel pair={pair} />
              </>
            ) : undefined}
          </div>
        </div>

        {/* Written for Pulse, rendered only by Pulse until now. It is the
            board's status bar, not that screen's. */}
        <PulseFooter />
      </div>
    </Screen>
  )
})

const Screen = styled.div`
  position: fixed;
  inset: 0;

  ${pulseV2Styles}
  ${tradeStyles}
`
